import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TopNavigation, { TopNavigationProps } from '@amzn/awsui-components-console/top-navigation';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import Toggle from '@amzn/awsui-components-console/toggle';
import { SearchPropertyFilter } from '../SearchEntityFilter';
import { HELP_FOR_SEARCH } from 'src/constants/ComponentConstants';
import { ButtonPopover } from '../ButtonPopover';
import { UserIcon } from '../UserIcon';
import { useAuthContext } from 'src/providers/AuthContext';
import { useReleaseNotesStore } from 'src/stores/releaseNotesStore';
import FontPreference, {
		applyFont,
		getStoredFont,
	 } from '../fontPreference/FontPreference';
import { THEME_ICONS, WORKFLOW_ICON } from './utilityIcons';
import { useUIPreferences } from 'src/stores/uiPreferencesStore';
import './navbarHeader.scss';

const DARK_THEME_KEY = 'isDarkTheme';
const DARK_MODE_CLASS = 'awsui-polaris-dark-mode';

export default function NavbarHeader() {
	const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
	const [showPreferences, setShowPreferences] = useState<boolean>(false);
	const authContext = useAuthContext();
	const { stripedRows, setStripedRows, sidePanelOpen, setSidePanelOpen } = useUIPreferences();
	const { hasUnseenUpdates, markAsSeen, fetchIfNeeded } = useReleaseNotesStore();

	useEffect(() => {
		fetchIfNeeded();
	}, [fetchIfNeeded]);

	useEffect(() => {
		const storedTheme = localStorage.getItem(DARK_THEME_KEY);
		const isDark = JSON.parse(storedTheme || 'false');

		if (isDark) {
			document.body.classList.add(DARK_MODE_CLASS);
		}

		setIsDarkMode(isDark);
		applyFont(getStoredFont());
	}, []);

	const handleDarkModeToggle = useCallback((): void => {
		const newTheme = !isDarkMode;

		setIsDarkMode(newTheme);
		localStorage.setItem(DARK_THEME_KEY, JSON.stringify(newTheme));
		document.body.classList.toggle(DARK_MODE_CLASS);
	}, [isDarkMode]);

	const handleWhatsNewClick = useCallback(() => {
		markAsSeen();
		window.location.href = '#/changes';
	}, [markAsSeen]);

	const handleUserIconClick = useCallback(() => {
		const username = authContext?.userName;
		if (username) {
			window.location.href = `#/dashboard?userId=${username}`;
		}
	}, [authContext?.userName]);

	const utilities: TopNavigationProps.Utility[] = useMemo(() => [
		{
			type: 'menu-dropdown',
			text: 'Workflows',
			iconSvg: WORKFLOW_ICON,
			items: [
				{
					id: 'execute-workflows',
					text: 'Execute Workflows',
					href: '#/executeWorkflows',
					iconName: 'play',
				},
				{
					id: 'manage-workflows',
					text: 'Manage Workflows',
					href: '#/manageWorkflows',
					iconName: 'folder',
				},
				{
					id: 'workflow-dashboard',
					text: 'Workflow Dashboard',
					href: '#/manageWorkflows/workflowDashboard',
					iconName: 'status-info',
				},
				{
					id: 'monitor-workflows',
					text: 'Monitor Workflows',
					href: '#/entitySearch',
					iconName: 'search',
				},
				{
					id: 'create-workflow',
					text: 'Create Workflow',
					href: '#/createWorkflow',
					iconName: 'add-plus',
				},
			],
		},
		{
			type: 'menu-dropdown',
			text: 'Help',
			iconName: 'contact',
			items: [
				{
					id: 'contact-us',
					text: 'Contact Us',
					href: '#/contact',
				},
				{
					id: 'faq',
					text: 'FAQ',
					href: 'https://w.amazon.com/bin/view/SNOW/FAQs',
					external: true,
					externalIconAriaLabel: 'Opens in a new tab',
				},
				{
					id: 'report-issue',
					text: 'Report an Issue',
					href: 'https://tiny.amazon.com/1k10o7m8w/riveamazHYD1work',
					external: true,
					externalIconAriaLabel: 'Opens in a new tab',
				},
				{
					id: 'oncall',
					text: 'Oncall',
					href: 'https://oncall.corp.amazon.com/#/view/snow-sde/schedule',
					external: true,
					externalIconAriaLabel: 'Opens in a new tab',
				},
			],
		},
		{
			type: 'button',
			text: 'Preferences',
			iconName: 'settings',
			ariaLabel: 'Preferences',
			onClick: () => setShowPreferences(true),
		},
		{
			type: 'button',
			iconName: 'notification',
			ariaLabel: "What's New",
			badge: hasUnseenUpdates,
			disableUtilityCollapse: true,
			onClick: handleWhatsNewClick,
		},
		{
			type: 'button',
			iconSvg: (
				<div className="user-icon-wrapper">
					<UserIcon
						user={authContext?.userName || 'currentUser'}
						size="small"
						iconOnly={true}
					/>
				</div>
			),
			ariaLabel: authContext?.userName || 'User profile',
			onClick: handleUserIconClick,
		},
	], [authContext?.userName, hasUnseenUpdates, handleWhatsNewClick, handleUserIconClick]);

	return (
		<div id="header" className="header-container">
			<TopNavigation
				identity={{
					href: '#/',
					title: 'SNOW',
					logo: {
						src: '/assets/logo.svg',
						alt: 'SNOW Logo',
					},
				}}
				data-testid="top-navigation"
				utilities={utilities}
				search={
					<div className="search-container">
						<SearchPropertyFilter />
						<ButtonPopover
							content={HELP_FOR_SEARCH}
							position="top"
							size="large"
						/>
					</div>
				}
				i18nStrings={{
					searchIconAriaLabel: 'Search',
					searchDismissIconAriaLabel: 'Close search',
					overflowMenuTriggerText: 'More',
					overflowMenuTitleText: 'All',
				}}
			/>

			<Modal
				visible={showPreferences}
				onDismiss={() => setShowPreferences(false)}
				header="Preferences"
				size="small"
			>
				<SpaceBetween size="l">
					<Box variant="small" color="text-body-secondary">
						Your preferences are persisted in your browser and may vary across
						devices.
					</Box>
					<FormField label="Font">
						<FontPreference />
					</FormField>
					<FormField label="Theme">
						<RadioGroup
							value={isDarkMode ? 'dark' : 'light'}
							onChange={({ detail }) => {
								const newIsDark = detail.value === 'dark';

								if (newIsDark !== isDarkMode) {
									handleDarkModeToggle();
								}
							}}
							items={[
								{ 
									value: 'light', 
									label: (
										<SpaceBetween direction="horizontal" size="xxs">
											<span>Light</span>
											{THEME_ICONS.light}
										</SpaceBetween>
									),
								},
								{ 
									value: 'dark', 
									label: (
										<SpaceBetween direction="horizontal" size="xxs">
											<span>Dark</span>
											{THEME_ICONS.dark}
										</SpaceBetween>
									),
								},
							]}
						/>
					</FormField>
					<FormField label="Tables">
						<Toggle
							checked={stripedRows}
							onChange={({ detail }) => setStripedRows(detail.checked)}
						>
							Striped rows by default
						</Toggle>
					</FormField>
					<FormField label="Navigation">
						<Toggle
							checked={sidePanelOpen}
							onChange={({ detail }) => setSidePanelOpen(detail.checked)}
						>
							Side panel visible by default
						</Toggle>
					</FormField>
					<Box variant="small" color="text-body-secondary">
						Logged in as: {authContext?.userName || 'Unknown'}
					</Box>
				</SpaceBetween>
			</Modal>

		</div>
	);
}
